import React from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { useTranslation } from 'react-i18next';

interface ClientHourItem {
  id: number;
  name: string;
  summarize?: {
    task: { name: string };
    hour_spent?: string | null;
  }[];
}

interface ClientNoHourItem {
  id: number;
  name: string;
  hour_spent?: string | null;
}

interface WorkPerformanceGraphProps {
  clientHour: ClientHourItem[];
  clientNoHour: ClientNoHourItem[];
  onShowClientHourChange: (value: boolean) => void;
  showClientHour: boolean;
}

const WorkPerformanceGraph: React.FC<WorkPerformanceGraphProps> = ({
  clientHour,
  clientNoHour,
  onShowClientHourChange,
  showClientHour,
}) => {
  const { t } = useTranslation('client');

  const parseDurationToHours = (duration: string) => {
    let totalHours = 0;
    if (duration.includes(' ')) {
      const [daysPart, timePart] = duration.split(' ');
      const days = parseFloat(daysPart);
      totalHours += days * 24;

      const [hours, minutes, seconds] = timePart.split(':').map(parseFloat);
      totalHours += hours + minutes / 60 + seconds / 3600;
    } else {
      const [hours, minutes, seconds] = duration.split(':').map(parseFloat);
      totalHours += hours + minutes / 60 + seconds / 3600;
    }
    return totalHours.toFixed(2);
  };

  const minVisibleValue = 10;

  const hourSpentData = clientHour.map((item) => {
    const tasks =
      item.summarize
        ?.map((summary) => {
          const hours = summary.hour_spent
            ? parseFloat(parseDurationToHours(summary.hour_spent))
            : 0;
          const adjustedHours =
            hours > 0 && hours < minVisibleValue ? minVisibleValue : hours;
          return {
            taskId: summary.task.name,
            actualValue: hours,
            displayValue: adjustedHours,
          };
        })
        .filter((task) => task.displayValue > 0) || [];
    return {
      clientId: item.id,
      clientName: item.name,
      tasks: tasks,
    };
  });

  const colors = [
    '#EDDDC0',
    '#A9C7F8',
    '#A26065',
    '#D4898F',
    '#D4AD07',
    '#6FE4B3',
    '#7DB826',
    '#196E69',
    '#6B9E5C',
    '#56DFBD',
    '#184863',
    '#F9FE7F',
    '#F1C2E0',
    '#4E9C7F',
    '#0331D1',
  ];

  const taskColorMap: { [key: string]: string } = {};
  let colorIndex = 0;

  clientHour.forEach((client) => {
    client.summarize?.forEach((summary) => {
      if (!taskColorMap[summary.task.name]) {
        taskColorMap[summary.task.name] = colors[colorIndex % colors.length];
        colorIndex++;
      }
    });
  });

  const chartData: ChartData<'bar'> = {
    labels: showClientHour
      ? hourSpentData.map((d) => d.clientName)
      : clientNoHour.map((item) => item.name),
    datasets: showClientHour
      ? hourSpentData.flatMap((client, clientIndex) =>
          client.tasks.map((task, taskIndex) => ({
            label: task.taskId,
            data: hourSpentData.map((_, i) =>
              i === clientIndex ? task.displayValue : 0
            ),
            backgroundColor: taskColorMap[task.taskId],
            borderColor: taskColorMap[task.taskId],
            borderWidth: 1,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
          }))
        )
      : [
          {
            label: 'ClientNoHour',
            data: clientNoHour.map((item) =>
              item.hour_spent
                ? parseFloat(parseDurationToHours(item.hour_spent))
                : 0
            ),
            backgroundColor: colors.map((color) => `${color}90`),
            borderColor: colors,
            borderWidth: 1,
            barPercentage: 0.9,
            categoryPercentage: 0.9,
          },
        ],
  };

  const getMaxValue = (data: number[]) => {
    const max = Math.max(...data);
    if (max <= 40) return 10;
    if (max <= 300) return 50;
    if (max <= 800) return 100;
    return 200;
  };

  const clientNoHourData = clientNoHour.map((item) =>
    item.hour_spent ? parseFloat(parseDurationToHours(item.hour_spent)) : 0
  );
  const clientHourData = hourSpentData.flatMap((client) =>
    client.tasks.map((task) => task.displayValue)
  );

  const combinedData = showClientHour ? clientHourData : clientNoHourData;
  const stepSize = getMaxValue(combinedData);

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: t('graph.unit'),
        align: 'start',
        position: 'top',
        padding: {
          top: 0,
          bottom: 10,
        },
        font: {
          size: 16,
          weight: 'bold',
        },
        color: '#333',
      },
      legend: {
        display: false,
        position: 'top' as const,
      },
      tooltip: {
        backgroundColor: '#ffffff',
        titleColor: '#333333',
        bodyColor: '#333333',
        borderColor: '#cccccc',
        borderWidth: 1,
        padding: 10,
        titleFont: {
          size: 24,
          weight: 'bold',
        },
        bodyFont: {
          size: 24,
        },
        callbacks: {
          title: () => '',
          label: (context) => {
            if (showClientHour) {
              return context.dataset.label || ''; // When tasks are shown
            } else {
              const clientNoHourItem = clientNoHour[context.dataIndex];
              return clientNoHourItem ? clientNoHourItem.name : ''; // When tasks are hidden
            }
          },
          afterLabel: (context) => {
            if (!showClientHour) {
              // Calculate total duration when "Show Tasks" is unchecked
              const clientNoHourItem = clientNoHour[context.dataIndex];
              const totalDuration = clientNoHourItem?.hour_spent
                ? `${parseDurationToHours(clientNoHourItem.hour_spent)} hours`
                : 'No hours logged';
              return totalDuration; // Display the total duration
            } else {
              // Existing logic for showing task-specific hours when "Show Tasks" is checked
              const clientIndex = context.dataIndex;
              const taskIndex = context.datasetIndex;
              const task = hourSpentData[clientIndex]?.tasks?.find(
                (t) => t.taskId === context.dataset.label
              );
              return task ? `${task.actualValue} hours` : ''; // Display task-specific duration
            }
          },
        },
      },
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
    },
    scales: {
      x: {
        stacked: false,
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
        },
        border: {
          color: 'black',
        },
      },
      y: {
        stacked: false,
        ticks: {
          stepSize: stepSize,
        },
        grid: {
          color: '#E0E0E0',
        },
        border: {
          color: 'black',
        },
        suggestedMax:
          Math.ceil(Math.max(...combinedData) / stepSize) * stepSize,
      },
    },
  };

  const clientWidth = 300;
  const visibleClients = 5;
  const totalClients = showClientHour ? clientHour.length : clientNoHour.length;
  const chartContainerWidth = totalClients * clientWidth;

  return (
    <div className="w-full">
      <div className="flex items-center justify-end">
        <label>
          <input
            type="checkbox"
            checked={showClientHour}
            onChange={(e) => onShowClientHourChange(e.target.checked)}
            className="mr-1"
          />
          {t('graph.show_task')}
        </label>
      </div>
      <div
        className="w-full overflow-x-auto"
        style={{
          maxWidth: `${visibleClients * clientWidth}px`,
          height: '36vh',
        }}
      >
        <div
          className="min-w-max h-full"
          style={{
            width: `${chartContainerWidth}px`,
          }}
        >
          <Bar data={chartData} options={options} />
        </div>
      </div>
    </div>
  );
};

export default WorkPerformanceGraph;
